import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Card, CardContent, Button } from '@pantry/ui';
import { useDashboardStats, useExpiringSoon, useResetTour } from '@pantry/api-client';
import { resolveDashboardExpiryDays } from '@pantry/shared';
import { useSelectedHousehold } from '../../contexts/HouseholdContext';
import { useTourContext } from '../../components/onboarding/useTourContext';
import {
  ArchiveBoxIcon,
  HomeIcon,
  PlusIcon,
  ClockIcon,
  ChartBarIcon,
  MapPinIcon,
  SparklesIcon,
  ArrowRightIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { ClipboardListIcon } from 'lucide-react';
import { HealthRing } from '../../components/dashboard/health-ring';
import { HealthBar } from '../../components/dashboard/health-bar';
import { StatCard } from '../../components/dashboard/stat-card';
import { QuickActionCard } from '../../components/dashboard/quick-action-card';
import { ExpiringSoonTimeline } from '../../components/dashboard/expiring-soon-timeline';

export const Route = createFileRoute('/_app/dashboard')({
  component: DashboardPage,
});

function DashboardPage() {
  const { t } = useLingui();
  const { selectedHousehold, isLoading: householdLoading } = useSelectedHousehold();
  const householdId = selectedHousehold?.id || '';

  const { startTour } = useTourContext();
  const resetTour = useResetTour();
  const [isStartingDashboardMiniTour, setIsStartingDashboardMiniTour] = useState(false);

  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError,
  } = useDashboardStats(householdId);

  const dashboardExpiryDays = resolveDashboardExpiryDays(selectedHousehold ?? null);

  const {
    data: expiringItems,
    isLoading: expiringLoading,
    error: expiringError,
  } = useExpiringSoon(householdId, { days: dashboardExpiryDays });

  const isLoading = householdLoading || statsLoading;

  const handleStartDashboardMiniTour = async () => {
    if (isStartingDashboardMiniTour) return;

    setIsStartingDashboardMiniTour(true);
    try {
      await resetTour.mutateAsync({ tourId: 'dashboard-basics', deviceType: 'web' });
      await startTour('dashboard-basics');
    } catch (error) {
      console.error('Failed to start dashboard mini tour:', error);
    } finally {
      setIsStartingDashboardMiniTour(false);
    }
  };

  const displayStats = stats;
  const displayExpiringItems = expiringItems;

  // Calculate inventory health percentage
  const totalItems = displayStats?.totalItems ?? 0;
  const expiringSoon = displayStats?.expiringSoon ?? 0;
  const expired = displayStats?.expired ?? 0;
  const freshItems = Math.max(0, totalItems - expiringSoon - expired);
  const healthPercentage = totalItems > 0 ? Math.round((freshItems / totalItems) * 100) : 100;

  // Show empty state if no household selected
  if (!householdLoading && !selectedHousehold) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-6">
        <div className="text-center max-w-md animate-in">
          <div className="mx-auto w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
            <HomeIcon className="h-10 w-10 text-primary" />
          </div>
          <h1 className="font-display text-3xl font-semibold text-foreground mb-3">
            <Trans>Welcome to TrackMyGroceries</Trans>
          </h1>
          <p className="text-muted-foreground mb-6">
            <Trans>
              Create or select a household to start tracking your food inventory and reduce waste.
            </Trans>
          </p>
          <Button asChild size="lg">
            <Link to="/households">
              <PlusIcon className="h-5 w-5 mr-2" />
              <Trans>Get Started</Trans>
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-8">
      {/* Hero Section - At a Glance */}
      <section className="animate-in" style={{ animationDelay: '0ms' }}>
        {/* Greeting */}
        <div className="mb-6" data-tour="welcome">
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-foreground">
            {getGreeting(t)}
          </h1>
          <p className="text-muted-foreground mt-1">
            {selectedHousehold
              ? t`Here's how your pantry is doing at ${selectedHousehold.name}.`
              : t`Here's how your pantry is doing.`}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-tour="stats-grid">
          {/* Inventory Health Card - Hero */}
          <Card
            className="sm:col-span-2 lg:col-span-2 overflow-hidden relative"
            data-tour="inventory-health"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent" />
            <CardContent className="p-6 relative">
              <div className="flex items-center gap-6">
                {/* Health Ring */}
                <div className="relative flex-shrink-0">
                  <HealthRing percentage={healthPercentage} size={120} isLoading={isLoading} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      {isLoading ? (
                        <div className="h-8 w-12 animate-pulse rounded bg-muted mx-auto" />
                      ) : (
                        <>
                          <span className="text-2xl font-bold text-foreground">
                            {healthPercentage}%
                          </span>
                          <span className="block text-xs text-muted-foreground">
                            <Trans>healthy</Trans>
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Health Summary */}
                <div className="flex-1 min-w-0">
                  <h2 className="font-heading font-semibold text-lg text-foreground mb-3">
                    <Trans>Inventory Health</Trans>
                  </h2>
                  <div className="space-y-2">
                    <HealthBar
                      label={t`Fresh`}
                      count={freshItems}
                      total={totalItems}
                      color="bg-fresh"
                      isLoading={isLoading}
                    />
                    <HealthBar
                      label={t`Expiring`}
                      count={expiringSoon}
                      total={totalItems}
                      color="bg-expiring-soon"
                      isLoading={isLoading}
                    />
                    <HealthBar
                      label={t`Expired`}
                      count={expired}
                      total={totalItems}
                      color="bg-expired"
                      isLoading={isLoading}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Items */}
          <StatCard
            title={t`Total Items`}
            value={stats?.totalItems ?? 0}
            subtitle={t`in your pantry`}
            icon={ArchiveBoxIcon}
            isLoading={isLoading}
            error={statsError}
            delay={100}
          />

          {/* Expiring Soon */}
          <StatCard
            title={t`Needs Attention`}
            value={expiringSoon + expired}
            subtitle={expiringSoon > 0 ? t`${expiringSoon} expiring soon` : t`All items fresh`}
            icon={ClockIcon}
            isLoading={isLoading}
            error={statsError}
            variant={expiringSoon + expired > 0 ? 'warning' : 'success'}
            delay={150}
            dataTour="expiring-soon"
          />
        </div>
      </section>

      {/* Quick Actions */}
      <section className="animate-in" style={{ animationDelay: '200ms' }} data-tour="quick-actions">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="font-heading font-semibold text-lg text-foreground">
            <Trans>Quick Actions</Trans>
          </h2>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => void handleStartDashboardMiniTour()}
            data-tour="dashboard-help-basics"
            aria-label={t`Start dashboard basics mini tour`}
            title={t`Dashboard basics help`}
            aria-busy={isStartingDashboardMiniTour}
            disabled={isStartingDashboardMiniTour}
          >
            <QuestionMarkCircleIcon className="h-5 w-5" />
          </Button>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <QuickActionCard
            title={t`Add Items`}
            description={t`Scan or enter new items`}
            icon={PlusIcon}
            href="/items/batch"
            variant="primary"
            delay={0}
            dataTour="add-item"
          />
          <QuickActionCard
            title={t`View Inventory`}
            description={t`Browse all your items`}
            icon={ArchiveBoxIcon}
            href="/items"
            delay={50}
          />
          <QuickActionCard
            title={t`Start Cooking`}
            description={t`Use items from your pantry`}
            icon={SparklesIcon}
            href="/meal-prep"
            delay={100}
          />
          <QuickActionCard
            title={t`Storage Locations`}
            description={t`Organize your spaces`}
            icon={MapPinIcon}
            href={`/households/${selectedHousehold?.id ?? ''}/storage-locations`}
            delay={150}
            dataTour="storage-locations"
          />
          <QuickActionCard
            title={t`Shopping Lists`}
            description={t`Plan your grocery run`}
            icon={ClipboardListIcon}
            href="/shopping-lists"
            delay={200}
          />
        </div>
      </section>

      {/* Priority Actions - Expiring Items */}
      {((displayExpiringItems && displayExpiringItems.length > 0) || expiringLoading) && (
        <section
          className="animate-in"
          style={{ animationDelay: '300ms' }}
          data-tour="needs-attention-list"
        >
          <ExpiringSoonTimeline
            expiringItems={displayExpiringItems ?? []}
            isLoading={expiringLoading}
            error={expiringError}
            householdId={householdId}
            dashboardExpiryDays={dashboardExpiryDays}
          />
        </section>
      )}

      {/* Tips Section */}
      <section className="animate-in" style={{ animationDelay: '400ms' }}>
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="flex flex-col sm:flex-row">
              <div className="flex-1 p-6">
                <div className="flex items-center gap-2 text-primary mb-2">
                  <ChartBarIcon className="h-5 w-5" />
                  <span className="text-sm font-medium uppercase tracking-wider">
                    <Trans>Tip</Trans>
                  </span>
                </div>
                <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                  <Trans>Reduce food waste by up to 40%</Trans>
                </h3>
                <p className="text-muted-foreground text-sm mb-4">
                  <Trans>
                    Use items before they expire by checking your dashboard regularly. Items
                    expiring soonest should be used first.
                  </Trans>
                </p>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/items">
                    <Trans>View All Items</Trans>
                    <ArrowRightIcon className="h-4 w-4 ml-2" />
                  </Link>
                </Button>
              </div>
              <div className="hidden sm:flex items-center justify-center p-6 bg-gradient-to-br from-primary/10 to-primary/5 w-48">
                <div className="text-6xl font-display font-bold text-primary/30">40%</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

/**
 * Get a time-appropriate greeting
 */
function getGreeting(t: ReturnType<typeof useLingui>['t']): string {
  const hour = new Date().getHours();
  if (hour < 12) return t`Good morning`;
  if (hour < 17) return t`Good afternoon`;
  return t`Good evening`;
}
